import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";

function formatDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, login } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user === undefined) return null; // AuthProvider still checking
  if (user) {
    const dest = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("Please enter your username and password.");
      return;
    }
    setSubmitting(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      const msg =
        formatDetail(err?.response?.data?.detail) ||
        err?.message ||
        "Login failed. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0F0F10] text-white flex items-center justify-center p-6 relative overflow-hidden"
      data-testid="login-page"
    >
      {/* Ambient gold glow */}
      <div className="pointer-events-none absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#E5A82E]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-[#E5A82E]/5 blur-3xl" />

      <div className="w-full max-w-md relative">
        <div className="bg-[#161618] border border-white/5 rounded-2xl shadow-2xl p-8 md:p-10">
          <div className="flex flex-col items-center mb-8">
            <div className="bg-white rounded-2xl p-3 mb-5 shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_dealer-stock-1/artifacts/o3em795n_WhatsApp%20Image%202026-07-02%20at%202.28.23%20PM.jpeg"
                alt="Car Dream"
                className="h-16 w-auto object-contain"
                data-testid="login-logo"
              />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-white">Welcome back</h1>
            <p className="text-sm text-white/50 mt-2">Sign in to manage your shop</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white/80 text-sm">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="bg-[#0F0F10] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5A82E] focus-visible:border-[#E5A82E]/60 h-11"
                data-testid="login-username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80 text-sm">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-[#0F0F10] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5A82E] focus-visible:border-[#E5A82E]/60 h-11 pr-11"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="login-toggle-password"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="text-sm text-[#FCA5A5] bg-[#3B1B1B]/60 border border-[#7F1D1D]/50 rounded-lg px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-[#E5A82E] hover:bg-[#D19722] text-[#1A1A1A] font-semibold shadow-md transition-all disabled:opacity-70"
              data-testid="login-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-white/40">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Secure single-admin access</span>
          </div>
        </div>

        <p className="text-center text-xs text-white/30 mt-6">
          Car Dream · Auto · Repair · Paint
        </p>
      </div>
    </div>
  );
}
